/**
 * Bridges fish-web's legacy activity-mode layer system to the ai-toolkit's
 * generic `LayersAdapter` contract, powering the `mapGetLayers`/
 * `mapToggleLayers` AI tools.
 *
 * The allowlist mirrors `layerService.getFilteredModeLayers()`'s filter —
 * a label, a thumbnail image, and a passing feature gate — so the AI can
 * never see or toggle a layer the user can't.
 */
import type {
  LayersAdapter,
  ToolboxLayer,
} from '@onxmaps/ai-toolkit/toolbox/definitions/mapSetLayers'
import { MODES } from '@/fish/lib/enums'
import { FISH_ACTIVITY_MODES } from '@/fish/lib/layers/activity-modes'
import layerService from '@/services/layer-service'
import { isActive } from '@/services/feature-flag-service'
import type { FeatureFlagKey } from '@/lib/feature-flags'

interface ActivityModeLayer {
  id: string
  label?: string
  byline?: string
  layerGateKey?: FeatureFlagKey
  img?: string
}

const getActivityModeLayers = (): ActivityModeLayer[] =>
  FISH_ACTIVITY_MODES[MODES.FRESHWATER].layers as ActivityModeLayer[]

const isAllowlisted = (layer: ActivityModeLayer): boolean =>
  !!layer.label &&
  !!layer.img &&
  (!layer.layerGateKey || !!isActive(layer.layerGateKey))

const getAllowlistedLayers = (): ActivityModeLayer[] =>
  getActivityModeLayers().filter(isAllowlisted)

const toToolboxLayer = (layer: ActivityModeLayer): ToolboxLayer | undefined => {
  const runtime = layerService.getLayer(layer.id) as
    | { active?: boolean; hasAccess?: boolean }
    | undefined
  if (!runtime) {
    return undefined
  }
  return {
    id: layer.id,
    name: layer.label,
    description: layer.byline,
    isActive: !!runtime.active,
    hasAccess: runtime.hasAccess ?? true,
    thumbnailUrl: layer.img,
  }
}

/**
 * Creates the `LayersAdapter` fish-web's `mapGetLayers`/`mapToggleLayers`
 * tools drive. Injected into the portable `lib/ai` module from the host.
 */
export function createLayersAdapter(): LayersAdapter {
  return {
    ready: () => layerService.layersReadyPromise,
    listToggleableLayers: () =>
      getAllowlistedLayers().flatMap((layer) => {
        const toolboxLayer = toToolboxLayer(layer)
        return toolboxLayer ? [toolboxLayer] : []
      }),
    getLayer: (id) => {
      const layer = getAllowlistedLayers().find((l) => l.id === id)
      return layer ? toToolboxLayer(layer) : undefined
    },
    setLayerActive: (id, active) => {
      const layer = getAllowlistedLayers().find((l) => l.id === id)
      const runtime = layer
        ? (layerService.getLayer(id) as
            | { active?: boolean; hasAccess?: boolean }
            | undefined)
        : undefined
      if (
        !runtime ||
        (runtime.hasAccess ?? true) === false ||
        !!runtime.active === active
      ) {
        return
      }
      layerService.toggleLayer(id)
    },
  }
}
